import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ChevronDown as ExpandMoreIcon } from 'lucide-react';
import { sessionService } from '../../services/sessionService';
import type { AgentContextSummaryDto } from '../../types/contextWindow';

export interface ContextSummaryListProps {
  sessionId: string;
}

/**
 * Displays stored context summaries for a session.
 */
export const ContextSummaryList = ({ sessionId }: ContextSummaryListProps) => {
  const [items, setItems] = useState<AgentContextSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await sessionService.getContextSummaries(sessionId);
        if (!isMounted) return;
        setItems(data);
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load context summaries');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400">
        {error}
      </div>
    );
  }

  if (!items.length) {
    return (
      <p className="text-sm text-muted-foreground">
        No summaries yet.
      </p>
    );
  }

  return (
    <div>
      {items.map((s) => (
        <Accordion key={s.id} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                Summary · {new Date(s.createdAt).toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">
                {s.originalTokenCount.toLocaleString()} → {s.compressedTokenCount.toLocaleString()} tokens ·{' '}
                {s.messagesSummarized} msgs
              </span>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <span className="text-xs text-muted-foreground block mb-2">
              Range: {new Date(s.messageRangeStart).toLocaleString()} – {new Date(s.messageRangeEnd).toLocaleString()}
            </span>
            <p className="text-sm whitespace-pre-wrap">
              {s.summaryText}
            </p>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};
